import { registerErrorDataGatherer, reportException, Severity, SkywardException } from "#skyward_error";
import { mobileAccess, virtualDir } from "#skyward_global";
import { getLayerFromElement, getOpenModalLayers, ModalLayer, setupDraggableLimits, showWarningDialog } from "#skyward_modalLayer";
import { closePopup, getCurrentPopupId, loadContent, LoadContentsSettings, notifyDOMLoad, popupList, registerLoadDOMCallback } from "#skyward_window";
import { decodeHtml } from "./skyward.base.extensions";
import { formatServerDate, isDate } from "./skyward.base.time";
import { load } from "./skyward.ui.ajaxRepeater";
import { get, selectRowByElement } from "./skyward.ui.browse";
import { getBrowseById } from "./skyward.ui.browse.list";
import { manageButtonClick, waitForFunction } from "./skyward.ui.buttonManager";
import { setUpChangeTracking } from "./skyward.ui.changeTracking";
import { CheckboxBrowse, getSerializedOverriddenCheckboxDatas } from "./skyward.ui.checkboxBrowse";
import { populateBoxes } from "./skyward.ui.codeeel";
import { getEditableFields, getOriginalValuesData, getValue, setValue, toggle } from "./skyward.ui.controls";
import { setFocus } from "./skyward.ui.dialogFocusManager";
import { getEditableData } from "./skyward.ui.editableBrowse";
import { registerEvents } from "./skyward.ui.events";
import { reloadReferenceByControlId } from "./skyward.ui.reference";
import * as tileViewEEL from "./skyward.ui.tileViewEEL";
import { displayValidationMessages } from "./skyward.ui.validation";
import { Deferred, JQueryInput } from "./types";

interface Workflow
{
    customButtonAction: (name: string, skipGoingToNextTask?: boolean, taskInstanceRedirect?: boolean) => JQuery.Promise<any>;
    saveData: (buttonData: Record<string, unknown> | null | undefined, skipGoingToNextTask?: boolean, taskInstanceRedirect?: boolean) => JQuery.Promise<any>;
}

export interface WorkflowInstance extends Record<string, unknown>
{
    workflowInstanceId: NumericString;
    taskInstanceId: NumericString;
}

type NumericString = `${number}`;

interface PendingWorkflowInstance
{
    id: string; // always "pendingWorkflows"
    skip: boolean; // always true
    items: { workflowInstanceId: NumericString, taskInstanceId: NumericString; }[];
}

export interface WorkflowData extends LoadContentParameters
{
    workflowInstanceId: number;
    screen: string;
    object: string;
    module: string;
}

interface LoadContentParameters extends Record<string, unknown>
{
    redirectURL: string;
    referrerURL: string;
    loadContentSettings: string | LoadContentsSettings; // passed to skyward.window.loadContent; if this is a string JSON.parse() will be called on it
}

interface WorkflowScreenValues
{
    id: string;
    warninghash: string;
    warninghashes: string;
    json: string;
    controls: string;
    editableFields: string;
}

interface EndWorkflowData extends Record<string, unknown>
{
    completeEventName: string;
    externalRedirectTargetBlankURL?: string;
    redirectURL: string;
    referrerURL: string;
    redirectSettings: string | Partial<LoadContentsSettings>;
    popoutURL?: string;
    updateControl?:
    {
        pageStateControl: string;
        primaryKey?: string | number
    }
}

const skyward = window.skyward;

export const workflowList = {} as Record<number, Workflow>;

const events =
{
    primaryButtonPressed,
    closeWorkflowPopup
};

registerEvents("workflow", events);
registerErrorDataGatherer("workflow", gatherDataForError);
registerLoadDOMCallback(init);

export function getPendingWorkflowInstances()
{
    const list: PendingWorkflowInstance[] = [];
    const workflowInstances: WorkflowInstance[] = [];

    const pendingWorkflows = $("[data-workflow-instance-id]");

    pendingWorkflows.each(function ()
    {
        const workflow = $(this);
        const workflowInstanceId = workflow.attr("data-workflow-instance-id") as NumericString | undefined;
        const taskInstanceId = workflow.find(".workflowContent").attr("data-workflow-task-instance-id") as NumericString | undefined;

        if (workflowInstanceId && taskInstanceId)
        {
            workflowInstances.push(
                {
                    workflowInstanceId: workflowInstanceId,
                    taskInstanceId: taskInstanceId
                });
        }
    });

    const data: PendingWorkflowInstance =
    {
        id: "pendingWorkflows",
        items: workflowInstances,
        skip: true
    };

    list.push(data);

    return list;
}

export function getTopMostWorkflow()
{
    return $(".modalLayerContainer .workflowPopup").last();
}

export function getTopMostWorkflowData()
{
    return getTopMostWorkflow().find(".workflow").data() as WorkflowData;
}

export function addDisableNavigationPromise(promise: Promise<void> | JQuery.Deferred<any> | JQueryPromise<any> | Deferred<any>, refocus = true)
{
    const workflow = getTopMostWorkflow();
    const toolbar = workflow.find(".toolbar");
    const workflowContent = workflow.find(".workflowContent:first");

    let disableWorkflowNavigationPromiseCount = (workflow.data("disableWorkflowNavigationPromiseCount") as number) || 0;

    toggle(toolbar.find(".button"), false);
    disableWorkflowNavigationPromiseCount += 1;
    workflow.data("disableWorkflowNavigationPromiseCount", disableWorkflowNavigationPromiseCount);

    promise = $.when(promise).always(() =>
    {
        disableWorkflowNavigationPromiseCount = (workflow.data("disableWorkflowNavigationPromiseCount") as number) || 0;
        disableWorkflowNavigationPromiseCount -= 1;
        workflow.data("disableWorkflowNavigationPromiseCount", disableWorkflowNavigationPromiseCount);

        if (disableWorkflowNavigationPromiseCount === 0)
        {
            toggle(toolbar.find(".button"), true);
            if (refocus)
            {
                const modalLayer = getLayerFromElement(workflowContent);

                if (modalLayer)
                {
                    setFocus(modalLayer);
                }
            }
        }
    }) as unknown as Promise<void>;
}

export function getSavedData(savedDataParam: Record<string, unknown> | null | undefined, workflowContent: JQuery<HTMLElement>)
{
    const savedData = savedDataParam || {};

    workflowContent.find("[name]:not(.doNotPost, .doNotPost *)").each(function ()
    {
        const element = $(this);
        const elementName = element.attr("name")!;

        if (elementName !== '')
        {
            let elementValue = getValue(element);

            if (elementValue instanceof Date || element.is("sky-datepicker"))
            {
                // TODO: Remove specific "datepicker" checks (first if & ! in 2nd) in PR 6145635
                if (element.is(".datepicker") && skyward.datepicker.isValidDateObject(elementValue))
                {
                    elementValue = formatServerDate(elementValue, !element.is(".datepicker"));
                }
                else if (!element.is(".datepicker") && isDate(elementValue))
                {
                    elementValue = formatServerDate(elementValue, !element.is("sky-datepicker"));
                }
                else if ((element.val() as string | undefined)?.trim() === "")
                {
                    elementValue = "";
                }
                else
                {
                    elementValue = new Date(NaN);
                }
            }

            if (!element.is(":radio") || elementValue) // Prevents radio buttons from overriding the selected one
            {
                savedData[elementName] = elementValue;
            }
        }
    });

    workflowContent.find(".checkboxBrowse").each(function ()
    {
        const checkboxBrowse = $(this);
        const browse = getBrowseById(checkboxBrowse.attr('id')!) as CheckboxBrowse;
        savedData[browse.workflowVariableName] = JSON.stringify(
        {
            defaultChecked: browse.defaultChecked,
            datas: getSerializedOverriddenCheckboxDatas(browse),
            hash: browse.workflowCheckboxHash,
            search: decodeHtml(browse.browseSearchCondition)
        });
    });

    workflowContent.find(".editablebrowse").each(function ()
    {
        const editableBrowse = $(this);
        const browseId = editableBrowse.attr("id")!;
        const editabledata = getEditableData(browseId);
        savedData[browseId] = editabledata;
    });

    return savedData;
}

export function getScreenValues(workflowContainer: JQuery<HTMLElement>, buttonData: Record<string, unknown> | null | undefined)
{
    let warningHash = workflowContainer.data("warninghash");
    if (warningHash !== undefined && typeof warningHash !== "string")
    {
        warningHash = JSON.stringify(workflowContainer.data("warninghash"));
    }

    const id = workflowContainer.data("workflow-task-instance-id");
    const originalValues = getOriginalValuesData("[data-workflow-task-instance-id='" + id + "']");

    return {
        id: id,
        warninghash: warningHash,
        warninghashes: JSON.stringify(workflowContainer.data("warninghashes") || {}),
        json: JSON.stringify(getSavedData(buttonData, workflowContainer)),
        controls: originalValues.controls,
        editableFields: getEditableFields(workflowContainer)
    } as WorkflowScreenValues;
}

export function closeWorkflowPopup()
{
    const topMostLayer = getTopMostWorkflow();

    if (topMostLayer.length > 0)
    {
        const workflowId = topMostLayer.find(".workflow").data("workflow-instance-id");
        delete workflowList[workflowId];
        const layer = getLayerFromElement(topMostLayer);
        if (layer)
        {
            popupList.shift();
            layer.options.forceClose = true;
            return layer.close();
        }
    }

    return true;
}

export function callLoadContentFromUrl(workflowData: LoadContentParameters | WorkflowData)
{
    return callLoadContentFromUrlInternal(workflowData.redirectURL, workflowData.referrerURL, workflowData.loadContentSettings);
}

export function customButtonAction(customButtonName: string)
{
    const workflow = getTopMostWorkflow();
    const workflowInstanceId = workflow.find(".workflow").data("workflow-instance-id") as number;
    const workflowListItem = workflowList[workflowInstanceId];

    if (workflowListItem)
    {
        return workflowListItem.customButtonAction(customButtonName);
    }

    return $.Deferred().reject().promise();
}

function callLoadContentFromUrlInternal(redirectURL?: string, referrerURL?: string, loadContentSettings?: Partial<LoadContentsSettings> | string, workflowPopupId?: string)
{
    if (!redirectURL && !referrerURL)
    {
        return $.Deferred().resolve().promise();
    }

    let url;

    if (!redirectURL || redirectURL === "")
    {
        url = referrerURL;

        if (window.location.origin + virtualDir + url === window.location.href.replace(window.location.hash, ""))  //redirectURL was not set and we are attempting to reload the page we are on
        {
            return $.Deferred().resolve().promise();
        }
    }
    else
    {
        url = redirectURL;
    }

    let data: Record<string, string> | null = null;

    if (url!.indexOf("?") > 0)
    {
        const paramUrl = url!.substring(url!.indexOf("?") + 1);
        const urlParameters = new URLSearchParams(paramUrl);
        urlParameters.delete("w");
        urlParameters.delete("p");

        if (urlParameters.size)
        {
            data = {};

            urlParameters.forEach((value, key) =>
            {
                data![key] = value;
            });
        }

        url = url!.substring(0, url!.indexOf("?")); //get rid of page state and window state params
    }

    const urlSegments = url!.split("/"); //split on slashes
    const area = urlSegments.length > 0 ? urlSegments[0] : null;
    const controller = urlSegments.length > 1 ? urlSegments[1] : null;
    const action = urlSegments.length > 2 ? urlSegments[2] : null;
    const id = urlSegments.length > 3 ? urlSegments[3] : null;
    const tab = urlSegments.length > 4 ? urlSegments[4] : null;

    if (tab)
    {
        data = { ...data, tab: tab };
    }

    let target = "";
    if (area?.toLowerCase() === "workflow" && controller?.toLowerCase() === "workflowinstance" && $("[data-workflow-instance-id='" + id + "']").length <= 0)//redirecting to another workflow, launch in a new popup
    {
        target = "popup";
    }
    else if ($(".workflowPopup").length <= 0) // All workflow tasks are completed
    {
        target = "full";
    }
    else //same workflow, new task
    {
        target = workflowPopupId ?? "";
    }
    const settingsObject = typeof loadContentSettings === "string" ? JSON.parse(loadContentSettings) : loadContentSettings;

    return loadContent(target, area!, controller!, action!, id, data, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, settingsObject);
}

function init(this: JQuery<HTMLElement>)
{
    this.find(".workflow").each(function (_index)
    {
        const workflow = $(this);
        const toolbar = workflow.find(".toolbar");
        const workflowContent = workflow.find(".workflowContent:first");
        const workflowNewWindowButton = workflow.find(".js-workflowNewWindowButton");
        const workflowInstanceId = workflow.data("workflowInstanceId") as number;
        const currentTaskInstanceIdWrapper: { currentTaskInstanceId: number | null } =
        {
            currentTaskInstanceId: null
        };
        const workflowPopup = workflow.parent('.workflowPopup');
        const workflowPopupId = workflowPopup.attr("id")!;

        if (mobileAccess)
        {
            workflowNewWindowButton.hide();
        }
        else
        {
            // Draggable
            setNewWindowOnClick();

            workflowPopup.draggable({
                cancel: '.workflowHeader > a',
                handle: '.workflowHeader',
                scroll: false
            });

            setupDraggableLimits(workflowPopup);
        }

        // use a registerLoadDOMCallback to update the role=form attribute.
        // We can't rely on workflow-contentLoaded since other content can subsequently load, such as browses.
        registerLoadDOMCallback(() =>
        {
            if (this === workflow[0])
            {
                setFormRoleOnWorkflowForAccessibility();
            }
        });

        // @MATTE: var moduleContentHeader used to be receiving the $ object below, but is never used aside from the .css() call, now chained.
        $("#currentPage label").css('display', 'inline-block');

        const previousButtonClicked = manageButtonClick(function ()
        {
            const deferredClick = $.Deferred();

            $.ajax(virtualDir + "workflow/TaskInstance/partialcommit",
                {
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        id: currentTaskInstanceIdWrapper.currentTaskInstanceId,
                        editableFields: getEditableFields(workflowContent)
                    },
                    activityIndicator: { isWorkflowAjaxRequest: true },
                    success: function (data)
                    {
                        //Clear out any custom workflow buttons
                        workflow.find(".dirty.workflowButton").each(function ()
                        {
                            $(this).remove();
                        });

                        if (data.previousTaskInstanceID)
                        {
                            var target = getTopMostWorkflow().attr('id')!;
                            waitForFunction(deferredClick, loadContent(target, "workflow", "taskinstance", "currentinstance", data.previousTaskInstanceID));
                        }
                        else
                        {
                            callLoadContentFromUrlInternal(data.redirectURL, undefined, data.redirectSettings, workflowPopupId);
                            deferredClick.resolve();
                        }
                    },
                    error: function ()
                    {
                        deferredClick.resolve();
                    }
                });

            return deferredClick.promise();
        });

        const confirmButtonClicked = manageButtonClick(function ()
        {
            return saveData(null);
        });

        const cancelButtonClicked = manageButtonClick(function ()
        {
            var controls = getOriginalValuesData("[data-workflow-task-instance-id='" + currentTaskInstanceIdWrapper.currentTaskInstanceId + "']");
            $("[data-pending-task-instance='" + currentTaskInstanceIdWrapper.currentTaskInstanceId + "']").remove();
            return $.ajax(virtualDir + "workflow/TaskInstance/cancel/" + currentTaskInstanceIdWrapper.currentTaskInstanceId,
                {
                    type: "POST",
                    data: controls,
                    activityIndicator: { hideGlass: true, isWorkflowAjaxRequest: true },
                    success: function (data)
                    {
                        closeWorkflowPopup();
                        $(document.body).trigger(data.cancelEventName);
                        if (data.redirectOnCancel)
                        {
                            callLoadContentFromUrlInternal(data.redirectURL, undefined, data.redirectSettings, workflowPopupId);
                        }
                    }
                });
        });

        const customButtonClicked = manageButtonClick(function ()
        {
            const button = $(this);
            if (button.attr("data-prevent-task-completion") !== "true")
            {
                const skipGoingToNextTask = button.attr("data-go-to-next-task") === "false";
                const taskInstanceRedirect = button.attr("data-task-instance-redirect") === "true";
                const customButtonName = button.attr("data-custom-button-name")!;
                return customButtonActionInternal(customButtonName, skipGoingToNextTask, taskInstanceRedirect, currentTaskInstanceIdWrapper, workflowContent);
            }

            return;
        });

        toolbar.on("click" as any, ".js-workflow__previous.dirty:not([ui-disabled])", null, previousButtonClicked)
            .on("click" as any, ".js-workflow__confirm.dirty:not([ui-disabled])", null, confirmButtonClicked)
            .on("click" as any, ".js-workflow__cancel", null, cancelButtonClicked)
            .on("click" as any, "a.workflowButton.dirty:not([ui-disabled])", null, customButtonClicked);

        setUpChangeTracking(workflowContent.filter(".js-changeTrackingRoot"));

        // If we are returning to a previously started task, the task instance id is attached to the div of workflowContent
        const pendingTaskInstanceId = workflowContent.data("workflowTaskInstanceId") as number;
        const pendingWorkflowInstances = $("#pendingWorkflows .instance");
        const pendingWorkflowData: Partial<WorkflowInstance> = {};

        if (pendingTaskInstanceId)
        {
            getPendingTaskInstance(pendingTaskInstanceId, workflowContent.data("taskInstanceDynamicPath"));
        }
        else if (pendingWorkflowInstances.length > 0)
        {
            for (const pendingWorkflowInstance of pendingWorkflowInstances)
            {
                const instance = $(pendingWorkflowInstance);
                loadPendingWorkflowAfterRefresh(instance);
            }
            pendingWorkflowInstances.remove();
        }
        else if (workflowInstanceId)
        {
            skyward.taskstatus.beginAsyncStatusReport(workflowInstanceId, endWorkflow, displayNextTask, null);
        }

        if (workflowInstanceId !== undefined)
        {
            workflowList[workflowInstanceId] =
            {
                customButtonAction: (name: string, skipGoingToNextTask?: boolean, taskInstanceRedirect?: boolean) =>
                {
                    return customButtonActionInternal(name, skipGoingToNextTask, taskInstanceRedirect, currentTaskInstanceIdWrapper, workflowContent);
                },
                saveData: saveData
            };
        }

        function setFormRoleOnWorkflowForAccessibility()
        {
            const buttonsThatCanSave =
                [
                    ".button .save",
                    ".button .saveAndForward",
                    ".button .update",
                    ".button .arrowRight",
                ];
            const hasSavingButtons = toolbar.find(buttonsThatCanSave.join(",")).filter(":visible").length > 0;
            const hasInputControls = workflowContent.find(":input").not("[type=hidden]").length > 0;
            const layerPseudoForm = workflow.closest(".layerPseudoForm");
            if (!hasSavingButtons || !hasInputControls)
            {
                layerPseudoForm.removeAttr("role");
                return;
            }

            const workflowTitleId = workflow.find(".workflowFriendlyName").addIds().attr("id")!;
            layerPseudoForm.attr("aria-labelledby", workflowTitleId);
            layerPseudoForm.attr("role", "form");
        }

        function setNewWindowOnClick()
        {
            const url = window.location.pathname;
            let urlArr: Array<string | null>;

            if (!$.isEmptyObject(url))
            {
                urlArr = url.split('/');
                if (urlArr.length <= 5)
                {
                    urlArr[5] = null;
                }
            }

            workflowNewWindowButton.on("click", function ()
            {
                loadContent('popout', urlArr[2]!, urlArr[3]!, urlArr[4] || "index", urlArr[5]);
            });
        }

        function customButtonActionInternal(name: string, skipGoingToNextTask: boolean | undefined, taskInstanceRedirect: boolean | undefined, currentTaskInstanceIdWrapper: { currentTaskInstanceId: number | null }, _workflowContent: JQuery<HTMLElement>)
        {
            const deferredClick = $.Deferred();

            $.ajax(`${virtualDir}workflow/TaskInstance/setcustombuttonvariable/`,
                {
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        id: currentTaskInstanceIdWrapper.currentTaskInstanceId,
                        customButtonName: name
                    },
                    activityIndicator: { isWorkflowAjaxRequest: true },
                    success: function (returnData)
                    {
                        const data: Record<string, unknown> = {};
                        data[returnData.variableName] = returnData.variableValue;
                        waitForFunction(deferredClick, saveData(data, skipGoingToNextTask, taskInstanceRedirect));
                    },
                    error: function ()
                    {
                        deferredClick.resolve();
                    }
                });

            return deferredClick.promise();
        }

        function displayNextTask(data?: Partial<WorkflowInstance>)
        {
            const workflowInstance: Partial<WorkflowInstance> = data || {};
            const target = workflowPopupId;
            return loadContent(target, "workflow", "taskinstance", "currentinstance", workflowInstance.taskInstanceId);
        }

        function endWorkflow(dataParam?: Record<string, unknown>, postponeRedirect?: boolean)
        {
            const data: Partial<EndWorkflowData> = dataParam || {};
            $("[data-pending-task-instance='" + currentTaskInstanceIdWrapper.currentTaskInstanceId + "']").remove();
            closeWorkflowPopup();

            if (data.completeEventName)
            {
                $(document.body).trigger(data.completeEventName, data);
            }

            let redirectFunction = undefined;

            const control = data.updateControl ? getControlForUpdate(data) : undefined;
            if ((control === undefined || control.length === 0) && (data.redirectURL || data.referrerURL || data.externalRedirectTargetBlankURL))
            {
                redirectFunction = function ()
                {
                    if (data.externalRedirectTargetBlankURL)
                    {
                        window.open(data.externalRedirectTargetBlankURL, "_blank");
                    }
                    else
                    {
                        callLoadContentFromUrlInternal(data.redirectURL, data.referrerURL, data.redirectSettings, workflowPopupId);
                    }
                };

                if (postponeRedirect !== true)
                {
                    redirectFunction();
                }
            }

            if (data.updateControl)
            {
                const updateElement = $("#" + data.updateControl.pageStateControl);
                if (data.popoutURL)
                {
                    const path = data.popoutURL.split("/");
                    const pathData = path.length > 4 ? { tab: path[4] } : {};
                    loadContent("popout", path[0], path[1], path[2], data.updateControl.primaryKey, pathData);
                }

                const controlIsBrowse = getBrowseById(data.updateControl.pageStateControl) !== undefined;
                const controlIsBrowseInDetailsPanel = getBrowseById("detailsPanel_" + data.updateControl.pageStateControl) !== undefined;

                if (controlIsBrowse || controlIsBrowseInDetailsPanel)//i just created a new record to add to a browse
                {
                    // This will run and update the browse's seek ID, even if we are in the process of redirecting, as long as the browse
                    // is on the page.
                    let browseId: string;
                    let browseData: Record<string, unknown>;
                    if (controlIsBrowse)
                    {
                        browseId = data.updateControl.pageStateControl;
                        browseData = {};
                        browseData.seekId = data.updateControl.primaryKey;
                        get(browseId, browseData);
                        reloadReferenceByControlId(browseId);
                    }

                    if (controlIsBrowseInDetailsPanel)
                    {
                        browseId = "detailsPanel_" + data.updateControl.pageStateControl;
                        browseData = {};
                        browseData.seekId = data.updateControl.primaryKey;
                        get(browseId, browseData);
                        reloadReferenceByControlId(browseId);
                    }
                }
                else if (updateElement.hasClass("ajaxRepeaterPlaceHolder"))
                {
                    load(updateElement);
                }
                else if (control && control.length > 0) //i need to update a control's value from my new object
                {
                    const handled = control.triggerHandler("workflowEnd", data);
                    if (!handled)
                    {
                        // Legacy Code - Avoid Using - Prefer "workflowEnd" Event
                        updateControlWithValue(data, control);
                        if (data.redirectURL || data.referrerURL)
                        {
                            callLoadContentFromUrlInternal(data.redirectURL, data.referrerURL, data.redirectSettings);
                        }
                    }
                }
                else if (updateElement && updateElement.length > 0)
                {
                    updateElement.triggerHandler("workflowEnd", data);
                }
            }

            if (postponeRedirect === true)
            {
                return redirectFunction;
            }

            return;
        }

        function getControlForUpdate(workflowData: Partial<EndWorkflowData>)
        {
            const containersToCheck =
            [
                "#moduleContent .toolContents",
                "#moduleContent .contentArea",
                "#moduleContent .dataObjectViewer",
                "#detailsPanel .contentArea",
                "#detailsPanel .dataObjectViewer",
                ".filterBuilder"
            ];
            const popupId = getCurrentPopupId();
            if (popupId >= 0)
            {
                containersToCheck.unshift("#popup" + popupId);
            }

            let control;
            let container;

            for (const containerToCheck of containersToCheck)
            {
                container = $(containerToCheck);
                if (container.length)
                {
                    control = container.find("[name='" + workflowData.updateControl?.pageStateControl + "']");

                    if (control.length)
                    {
                        break;
                    }
                }
            }
            return control;
        }

        async function updateControlWithValue(workflowData: Partial<EndWorkflowData>, control: JQuery<HTMLElement>)
        {
            const pk = workflowData.updateControl?.primaryKey;
            const controlData = control.data();

            let area = controlData.area;
            let controller = controlData.controller;
            let action = controlData.action;

            let controlIsTileViewEEL = false;
            if (control.hasClass("codeEELValue"))
            {
                setValue(control.closest(".codeEEL"), pk);
                return;
            }

            let postData: Record<string, unknown> = {};
            postData["securityHash"] = controlData.securityHash;
            if (control.hasClass('tileViewEEL_search'))
            {
                controlIsTileViewEEL = true;
                area = controlData.updateControlAfterWorkflowArea;
                controller = controlData.updateControlAfterWorkflowController;
                action = controlData.updateControlAfterWorkflowAction;

                $.extend(postData, tileViewEEL.getPostData(controlData as tileViewEEL.TileViewEELSearchData, control));
            }

            if (!area || !controller || !action)
            {
                reportException(new SkywardException(
                    `Control ${control.attr("name") ?? control.attr("id")} was set to update after the workflow ${workflowData.completeEventName} id ${workflowData.workflowInstanceID} ` +
                    `completed, but it is missing required URL path parameters.`,
                    "",
                    Severity.Error,
                    "fafa5ed8-d71e-48e6-a3e9-c448d64bd3e4"));
                control.find(".codeEELValue").val(pk!);
                return;
            }

            const data = await $.ajax(
            {
                url: `${virtualDir}${area}/${controller}/${action}/${pk}`,
                type: "POST",
                data: postData,
                dataType: "JSON",
            });

            if (controlIsTileViewEEL)
            {
                tileViewEEL.populateBoxes(control.parent().parent(), data, pk!);
                return;
            }
            // Note: this code is probably unreachable. Code EELs have already been handled and nothing in the system has the action "AccountDimensionEEL"
            // TODO (Q2 2026) if there have been no info logs, this is safe to delete
            reportException(new SkywardException("Reached fallback code in workflow.js updateControlWithValue()", undefined, Severity.Info, "2c72d112-87df-45a7-adfb-11e8abf62d57"));
            const eelValue = control.hasClass("codeEELValue") ? control : control.find(".codeEELValue");
            const eelParent = eelValue.parent();
            eelValue.val(pk!);
            if (controlData.action === "AccountDimensionEEL")
            {
                // @ts-ignore accountDimensionEEL doesn't have a .d.ts file
                skyward.accountDimensionEEL.populateBoxes(eelParent, data);
            }
            else
            {
                populateBoxes(eelParent, data, true);
            }
        }

        function moveRefrenceAreaAboveWorkflowContent(dataContent: JQuery<HTMLElement>, workflowContent: JQuery<HTMLElement>)
        {
            const dontMoveClassName = ".js-refArea--dont-move";
            const contentReference = dataContent.find('.contentReference:not("' + dontMoveClassName + '"), .refArea:not("' + dontMoveClassName + '")');
            if (contentReference.length)
            {
                contentReference.addClass("refArea--workflow").insertBefore(workflowContent);

                //Since ref areas are added outside of workflowContent we need to call notifyDOMLoad on them directly.  This will no longer be necessary when skyward.ui.reference.ts is moved out of the bundle
                if (contentReference.hasClass('contentReference'))
                {
                    notifyDOMLoad("[data-workflow-instance-id='" + workflowInstanceId + "'] .contentReference");
                }
                if (contentReference.hasClass('refArea'))
                {
                    notifyDOMLoad("[data-workflow-instance-id='" + workflowInstanceId + "'] .refArea");
                }
            }
        }

        function moveErrorsToTopOfWorkflowContent()
        {
            const errorList = workflow.find('#baseWorkflowErrorContainer');
            if (errorList.length > 0)
            {
                errorList.parent().prepend(errorList);
            }
        }

        function getPendingTaskInstance(taskInstanceId: number, taskInstanceDynamicPath?: string)
        {
            currentTaskInstanceIdWrapper.currentTaskInstanceId = taskInstanceId;

            // Even though we have it marked not to cache it is still being cached
            // This is a temp work around until there is a solution. (Chrome issue)
            // Does not seem to be an issue with IE or Firefox.
            // http://bugs.jquery.com/ticket/12550
            const time = (new Date()).getTime();
            let url = "workflow/TaskInstance/DetailsContent/";

            if (taskInstanceDynamicPath)
            {
                url = taskInstanceDynamicPath;
            }

            let workflowLoadingPromise = $.Deferred();
            addDisableNavigationPromise(workflowLoadingPromise);

            $.ajax(virtualDir + url + currentTaskInstanceIdWrapper.currentTaskInstanceId + "?renderType=contentOnly&blah=" + time,
                {
                    type: "POST",
                    dataType: "viewpartHtml",
                    activityIndicator: { isWorkflowAjaxRequest: true },
                    // This is set to tell ContentMaster.sxl to show available bindings. These are also used in BaseController.cs to setup Field Restrictions.
                    data: { inWorkflow: true, currentTaskInstanceId: currentTaskInstanceIdWrapper.currentTaskInstanceId },
                    complete: function ()
                    {
                        workflowLoadingPromise.resolve();
                    },
                    success: function (data)
                    {
                        const dataContent = $('<div>').append(data);
                        moveRefrenceAreaAboveWorkflowContent(dataContent, workflowContent);
                        workflowContent.append(dataContent.children());
                        moveErrorsToTopOfWorkflowContent();
                        notifyDOMLoad("[data-workflow-instance-id='" + workflowInstanceId + "'] .workflowContent", { skywardContentPanel: true });

                        workflow.trigger("workflow-contentLoaded");

                        if (!mobileAccess)
                        {
                            const layer = getLayerFromElement(workflowContent);
                            if (layer)
                            {
                                const initialFocusElement = $(layer.options.initialFocusElement!);
                                if (!initialFocusElement.length || !layer.container.get(0)!.contains(initialFocusElement[0]))
                                {
                                    // initialFocusElement doesn't exist or isn't in the current workflow step
                                    layer.options.initialFocusElement = workflowContent.find(":tabbable").first();
                                }
                                setFocus(layer);
                                initKeyboardClose(layer);
                            }
                        }
                    }
                });
        }

        function initKeyboardClose(layer: ModalLayer)
        {
            const origBeforeClose = layer.beforeClose;
            layer.beforeClose = function ()
            {
                $(document).off("keydown", handleKeypress);
                if (typeof origBeforeClose === "function")
                {
                    return origBeforeClose.apply(null, arguments as any);
                }
            };

            $(document).on("keydown", handleKeypress);

            function handleKeypress(event: JQuery.KeyboardEventBase)
            {
                const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
                if (eventKeys.ESC && escapeShouldCloseWorkflow(layer))
                {
                    showWarningDialog(
                        "close this Workflow",
                        "Any changes will not be saved.",
                        { onConfirm: cancelButtonClicked });
                }
            }

            function escapeShouldCloseWorkflow(layer: ModalLayer)
            {
                const isValidLayer = typeof layer === "object" && layer.container instanceof $ && layer.contents instanceof $;
                if (isValidLayer)
                {
                    const isLastOpenLayer = getOpenModalLayers().last().is(layer.container);
                    if (isLastOpenLayer)
                    {
                        const hasCancelButton = layer.contents.find(".js-workflow__cancel").not(".ui-disabled").length > 0;
                        if (hasCancelButton)
                        {
                            return true;
                        }
                    }
                }
                return false;
            }
        }

        function saveData(buttonData: Record<string, unknown> | null | undefined, skipGoingToNextTask?: boolean, taskInstanceRedirect?: boolean)
        {
            const originalValues = getOriginalValuesData("[data-workflow-task-instance-id='" + currentTaskInstanceIdWrapper.currentTaskInstanceId + "']");
            const savedData = getSavedData(buttonData, workflowContent);
            const deferredClick = $.Deferred();

            if (!skyward.dataobjectviewer.preSaveValidation(workflowContent))
            {
                return deferredClick.resolve();
            }

            let warningHash = workflowContent.data("warninghash");
            if (warningHash !== undefined && typeof warningHash !== "string")
            {
                warningHash = JSON.stringify(workflowContent.data("warninghash"));
            }

            $.ajax(virtualDir + "workflow/TaskInstance/commit",
                {
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        id: currentTaskInstanceIdWrapper.currentTaskInstanceId,
                        warninghash: warningHash,
                        warninghashes: JSON.stringify(workflowContent.data("warninghashes") || {}),
                        json: JSON.stringify(savedData),
                        controls: originalValues.controls,
                        editableFields: getEditableFields(workflowContent),
                        workflowTemplateID: workflow.find(".js-workflowTemplateMenu-TemplateEEL").val()
                    },
                    activityIndicator: { isWorkflowAjaxRequest: true },
                    success: function (data)
                    {
                        switch (data.workflowStatus)
                        {
                            case "F":
                                // Invalid
                                const baseWarningError = workflow.find("#baseWorkflowErrorContainer");
                                displayValidationMessages(data, workflowContent, baseWarningError);
                                deferredClick.resolve();
                                break;
                            default:
                                //InProgress (Or Completed, if it ever were to happen)
                                workflowContent.data("workflowTaskInstanceId", "");

                                if (taskInstanceRedirect && data.redirectURL)
                                {
                                    waitForFunction(deferredClick, callLoadContentFromUrlInternal(data.redirectURL, undefined, undefined, workflowPopupId));
                                }
                                else if (skipGoingToNextTask)
                                {
                                    closeWorkflowPopup();
                                    window.location.reload();
                                }
                                else
                                {
                                    waitForFunction(deferredClick, skyward.taskstatus.beginAsyncStatusReport(workflowInstanceId, endWorkflow, displayNextTask, null));
                                }
                                break;
                        }
                    },
                    error: function ()
                    {
                        deferredClick.resolve();
                    }
                });
            return deferredClick.promise();
        }

        function loadPendingWorkflowAfterRefresh(instance: JQuery<HTMLElement>)
        {
            const taskInstanceId = instance.data("pendingTaskInstance");
            const workflowInstance = instance.data("pendingWorkflowInstance");
            if ($("[data-workflow-instance-id='" + workflowInstance + "']").length <= 0)
            {
                loadContent("popup", "Workflow", "WorkflowInstance", "CurrentInstance", workflowInstance,
                    undefined,
                    undefined,
                    undefined,
                    function ()
                    {
                        if ($("[data-workflow-task-instance-id='" + taskInstanceId + "']").length >= 0)
                        {
                            pendingWorkflowData.taskInstanceId = taskInstanceId;
                            displayNextTask(pendingWorkflowData);
                        }
                    },
                    undefined,
                    undefined,
                    function (layer)
                    {
                        closePopup(layer);
                        instance.remove();
                    });
            }
        }
    });
}

function primaryButtonPressed(this: JQueryInput)
{
    const elem = $(this);
    selectRowByElement(elem);
    const workflowId = elem.closest(".workflow").data("workflow-instance-id");

    const data = elem.data();
    const workflow = workflowList[workflowId];

    if (data.workflowVariableValue !== undefined && data.workflowVariableToSet !== undefined)
    {
        const saveData: Record<string, unknown> = {};
        saveData[data.workflowVariableToSet] = data.workflowVariableValue;
        workflow.saveData(saveData);
    }
    else if (data.workflowCustomButtonName !== undefined)
    {
        workflow.customButtonAction(data.workflowCustomButtonName);
    }
}

function gatherDataForError()
{
    const workflows = $(".workflowPopup .workflow").toArray().map(function (workflowElem)
    {
        const workflow = $(workflowElem);
        const data = workflow.data();
        const workflowPopup = workflow.parent('.workflowPopup');
        const workflowPopupId = workflowPopup.attr("id");
        const name = workflow.find(".workflowFriendlyName").text();
        const description = workflow.find(".taskDescription").text();
        const workflowContentData = workflow.find(".workflowContent").data();
        return {
            workflowInstanceId: data.workflowInstanceId,
            popupId: workflowPopupId,
            taskInstanceId: workflowContentData.taskInstanceId,
            taskInstanceDynamicPath: workflowContentData.taskInstanceDynamicPath,
            name: name,
            description: description
        };
    });

    return {
        openWorkflows: workflows,
        pendingWorkflows: getPendingWorkflowInstances()[0].items,
    };
}